import config from '~~/configs/collections.config';
import type {
  ICmsSectionCarousel,
  ICmsSectionGrid,
  ICmsSectionSpotlight,
} from '~~/types/_cms-types';

// invalidate frontend cache after 5 minutes
// const getTimedCacheKey = (baseKey: string): string => {
//   const now = Date.now();
//   const timeoutTime = 5 * 60 * 1000; // 5 minutes
//   const rounded = Math.floor(now / timeoutTime) * timeoutTime;
//   return `${baseKey}-${rounded}`;
// };

const useAdditionalTeasers = async (
  cacheKeyPrefix: string,
  sectionData: ICmsSectionGrid | ICmsSectionSpotlight | ICmsSectionCarousel,
  maxItems: number,
  manualItemIds?: number[],
  sort: string = '-publish_time',
  collection: 'articles' | 'dossiers' = 'articles',
  showShorts: boolean = false,
) => {
  const { objectKeysToCamelCase } = formatUtils();
  const cms = useCms();
  const route = useRoute();

  const hasBanner = 'banner' in sectionData && sectionData.banner != null;
  const filterType = sectionData.filterType;

  // Check cache for the requested items
  const cacheKey = `${cacheKeyPrefix}_${filterType}_${(manualItemIds || []).join('')}_${hasBanner}_${maxItems}`;
  // cacheKey = getTimedCacheKey(cacheKey);
  const cachedItem = useNuxtData(cacheKey).data.value;
  let items = cachedItem ? objectKeysToCamelCase(cachedItem) : undefined;

  if (items) return items;

  // do not get articles marked as 'exclude_from_home' for the homepage.
  const buildExcludeFromHomeFilter = () => {
    if (route.path === '/' && collection === 'articles') {
      return { exclude_from_home: { _neq: true } };
    }

    return {};
  };

  const buildDynamicFilter = () => {
    switch (sectionData.filterType) {
      case 'tags':
        if (sectionData.tag) {
          return { tags: { tags_id: { _eq: sectionData.tag.id } } };
        }
        break;
      case 'dossiers':
        if (sectionData.dossier) {
          return { dossiers: { dossiers_id: { _eq: sectionData.dossier.id } } };
        }
        break;
      case 'article_type':
        if (sectionData.articleType) {
          return { article_type: { _eq: sectionData.articleType } };
        }
        break;
    }
    return {};
  };

  const buildNinFilter = () => {
    if (manualItemIds && manualItemIds.length > 0) {
      return {
        id: {
          _nin: manualItemIds,
        },
      };
    }

    return {};
  };

  const buildArticleTypeFilter = () => {
    if (sectionData.filterType == 'tags' || sectionData.filterType == 'dossiers') {

      const excludedArticleTypes = ['paginas', 'peilingtrends'];
      if (!showShorts) {
        excludedArticleTypes.push('shorts');
      }

      return { article_type: { _nin: excludedArticleTypes } };
    }
    return {};
  };

  if (filterType === 'manual') return [];

  const filter = {
    _and: [
      buildNinFilter(),
      buildDynamicFilter(),
      buildArticleTypeFilter(),
      buildExcludeFromHomeFilter(),
    ],
  };

  const limit = maxItems - (manualItemIds?.length ?? 0) - (hasBanner ? 1 : 0);

  if (limit > 0) {
    // Fetch data from Directus and format it
    const { data, error } = await useAsyncData(cacheKey, () =>
      cms.readItems(collection, {
        fields: collection === 'articles' ? config.teaser : config[`${collection}Teaser`],
        filter,
        limit: maxItems - (manualItemIds?.length ?? 0) - (hasBanner ? 1 : 0),
        sort: [sort],
      }),
    );

    // A failing section must not take the whole page down (SSR 500): render it without extra items
    if (error.value) {
      console.warn(`Error fetching additional teasers (${cacheKey}):`, error.value);
      return [];
    }

    items = data.value ? objectKeysToCamelCase(data.value) : undefined;
  }

  return items || [];
};

export default useAdditionalTeasers;
